import React, { useState, useEffect } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { Coins, Check, X, AlertTriangle, Search, Gift, Loader2 } from "lucide-react";
import api from "@/lib/api";

const METHOD_LABEL = { wave: "Wave", orange_money: "Orange Money", interac: "Interac", card: "Carte" };
const JOB_LABEL = { completed: "réussie", failed: "échouée", processing: "en cours" };

const MerchantVideoSearch = () => {
  const [q, setQ] = useState("");
  const [busy, setBusy] = useState(false);
  const [results, setResults] = useState(null);

  const search = async () => {
    if (q.trim().length < 2) { toast.error("Tapez au moins 2 caractères."); return; }
    setBusy(true);
    try {
      const { data } = await api.get("/admin/merchant-video-status", { params: { q: q.trim() } });
      setResults(data.results);
    } catch { toast.error("Recherche impossible."); }
    finally { setBusy(false); }
  };

  const reoffer = async (bid) => {
    try {
      await api.post(`/admin/merchant-video-status/${bid}/reoffer`);
      toast.success("Vidéo offerte réactivée pour ce marchand !");
      search();
    } catch { toast.error("Action impossible."); }
  };

  return (
    <div className="rounded-xl bg-white/5 p-3 mb-4" data-testid="admin-merchant-video-search">
      <p className="text-sm font-bold mb-2 flex items-center gap-1.5"><Gift className="h-4 w-4 text-[#FFBA08]" /> Vidéo offerte — vérifier un marchand</p>
      <div className="flex gap-2 mb-2">
        <input
          data-testid="merchant-video-search-input"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          onKeyDown={(e) => e.key === "Enter" && search()}
          placeholder="Nom ou lien de la boutique (ex. Au moins cher)"
          className="h-10 px-3 rounded-xl bg-white/10 text-sm outline-none border border-transparent focus:border-[#FFBA08] flex-1"
        />
        <button data-testid="merchant-video-search-btn" onClick={search} disabled={busy} className="h-10 px-4 rounded-xl bg-white/10 font-semibold text-sm flex items-center gap-1.5 disabled:opacity-50">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Search className="h-4 w-4" />} Chercher
        </button>
      </div>
      {results && results.length === 0 && <p className="text-white/40 text-sm" data-testid="merchant-video-no-result">Aucune boutique trouvée.</p>}
      {results && results.map((b) => (
        <div key={b.business_id} className="rounded-lg bg-white/5 p-3 mb-2 flex flex-wrap items-center gap-3" data-testid={`merchant-video-result-${b.business_id}`}>
          <div className="flex-1 min-w-[190px]">
            <p className="text-sm font-bold">{b.name} <span className="text-white/40 font-normal">({b.country})</span></p>
            <p className="text-xs text-white/60">
              Solde : <span className="font-semibold">{b.video_credits} cr.</span> · Vidéo offerte :{" "}
              {b.free_video_available
                ? <span className="text-[#95D5B2] font-semibold">disponible</span>
                : <span className="text-[#FFBA08] font-semibold">déjà utilisée</span>}
            </p>
            {b.recent_jobs?.length > 0 && (
              <p className="text-[11px] text-white/40">
                Dernières vidéos : {b.recent_jobs.map((j) => JOB_LABEL[j.status] || j.status).join(", ")}
              </p>
            )}
          </div>
          {!b.free_video_available && (
            <button
              data-testid={`merchant-video-reoffer-${b.business_id}`}
              onClick={() => reoffer(b.business_id)}
              className="h-9 px-3 rounded-lg bg-[#2D6A4F] text-sm font-semibold flex items-center gap-1.5"
            >
              <Gift className="h-4 w-4" /> Ré-offrir la 1ʳᵉ vidéo
            </button>
          )}
        </div>
      ))}
    </div>
  );
};

export const AdminVideoCredits = () => {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ["admin-credit-purchases"],
    queryFn: async () => (await api.get("/admin/credit-purchases")).data,
    refetchInterval: 30000,
  });
  const [wave, setWave] = useState("");
  const [om, setOm] = useState("");
  const [interac, setInterac] = useState("");
  const [threshold, setThreshold] = useState("");
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    if (data?.settings && !loaded) {
      setWave(data.settings.wave_number || "");
      setOm(data.settings.om_number || "");
      setInterac(data.settings.interac_dest || "");
      setThreshold(String(data.settings.video_threshold ?? 1200));
      setLoaded(true);
    }
  }, [data, loaded]);

  const saveSettings = async () => {
    try {
      await api.put("/admin/credit-settings", {
        wave_number: wave, om_number: om, interac_dest: interac,
        video_threshold: Number(threshold) >= 0 ? Number(threshold) : undefined,
      });
      toast.success("Réglages enregistrés.");
      qc.invalidateQueries({ queryKey: ["admin-credit-purchases"] });
    } catch { toast.error("Enregistrement impossible."); }
  };

  const act = async (id, action) => {
    try {
      await api.post(`/admin/credit-purchases/${id}/${action}`);
      toast.success(action === "confirm" ? "Paiement confirmé — crédits ajoutés au marchand !" : "Demande rejetée.");
      qc.invalidateQueries({ queryKey: ["admin-credit-purchases"] });
    } catch { toast.error("Action impossible."); }
  };

  const pending = data?.pending || [];
  const providerBal = data?.video_balance;
  const thNum = Number(data?.settings?.video_threshold ?? 1200);
  const balLow = typeof providerBal === "number" && providerBal <= thNum;

  return (
    <div className="bg-white/5 rounded-2xl p-4 mb-6" data-testid="admin-video-credits">
      <div className="flex items-center gap-2 mb-1 flex-wrap">
        <Coins className="h-5 w-5 text-[#FFBA08]" />
        <h2 className="font-display text-lg font-bold">Crédits vidéo — recharges à valider</h2>
        {pending.length > 0 && (
          <span className="ml-1 h-6 px-2 rounded-full bg-[#E85D04] text-xs font-bold flex items-center" data-testid="credit-pending-count">{pending.length}</span>
        )}
        {typeof providerBal === "number" && (
          <span
            data-testid="video-provider-balance-chip"
            className={`ml-auto h-7 px-3 rounded-full text-xs font-bold flex items-center gap-1.5 ${balLow ? "bg-[#D00000]/80 text-white" : "bg-white/10 text-white/80"}`}
          >
            {balLow && <AlertTriangle className="h-3.5 w-3.5" />}
            Solde Runway : {providerBal} crédits
          </span>
        )}
      </div>
      <p className="text-xs text-white/50 mb-3">Afrique : Mobile Money (validation manuelle ici, 1 crédit = 40 FCFA). Canada : carte bancaire (crédit automatique) ou Interac (validation manuelle ici). Une alerte email vous est envoyée si le solde Runway passe sous le seuil (1 vidéo UGC de 15 s ≈ 588 crédits Runway).</p>

      <MerchantVideoSearch />

      <div className="flex flex-wrap gap-2 mb-4">
        <input
          data-testid="admin-wave-number"
          value={wave}
          onChange={(e) => setWave(e.target.value)}
          placeholder="Numéro Wave (réception)"
          className="h-10 px-3 rounded-xl bg-white/10 text-sm outline-none border border-transparent focus:border-[#FFBA08] flex-1 min-w-[180px]"
        />
        <input
          data-testid="admin-om-number"
          value={om}
          onChange={(e) => setOm(e.target.value)}
          placeholder="Numéro Orange Money (réception)"
          className="h-10 px-3 rounded-xl bg-white/10 text-sm outline-none border border-transparent focus:border-[#FFBA08] flex-1 min-w-[180px]"
        />
        <input
          data-testid="admin-interac-dest"
          value={interac}
          onChange={(e) => setInterac(e.target.value)}
          placeholder="Interac : téléphone ou email (réception, Canada)"
          className="h-10 px-3 rounded-xl bg-white/10 text-sm outline-none border border-transparent focus:border-[#FFBA08] flex-1 min-w-[200px]"
        />
        <input
          data-testid="admin-video-threshold"
          value={threshold}
          onChange={(e) => setThreshold(e.target.value.replace(/[^0-9]/g, ""))}
          placeholder="Seuil d'alerte Runway"
          title="Seuil d'alerte : email envoyé quand le solde Runway passe sous ce nombre de crédits"
          className="h-10 px-3 rounded-xl bg-white/10 text-sm outline-none border border-transparent focus:border-[#FFBA08] w-40"
        />
        <button data-testid="admin-save-credit-numbers" onClick={saveSettings} className="h-10 px-4 rounded-xl bg-white/10 font-semibold text-sm">Enregistrer</button>
      </div>

      {pending.length === 0 ? (
        <p className="text-white/40 text-sm" data-testid="credit-no-pending">Aucune recharge en attente.</p>
      ) : (
        <div className="space-y-2">
          {pending.map((p) => (
            <div key={p.purchase_id} className="rounded-xl bg-white/5 p-3 flex flex-wrap items-center gap-3" data-testid={`credit-purchase-${p.purchase_id}`}>
              <div className="flex-1 min-w-[200px]">
                <p className="text-sm font-bold">{p.business_name || p.business_id}</p>
                <p className="text-xs text-white/60">
                  {p.credits} crédits · <span className="text-[#FFBA08] font-semibold">{(p.amount ?? p.amount_fcfa ?? 0).toLocaleString("fr-FR")} {p.currency === "CAD" ? "$CA" : "FCFA"}</span> · {METHOD_LABEL[p.method] || p.method}
                  {p.reference && <> · Réf : <span className="text-white/80">{p.reference}</span></>}
                </p>
                <p className="text-[11px] text-white/40">{new Date(p.created_at).toLocaleString("fr-FR")}</p>
              </div>
              <div className="flex gap-2">
                <button
                  data-testid={`credit-confirm-${p.purchase_id}`}
                  onClick={() => act(p.purchase_id, "confirm")}
                  className="h-9 px-3 rounded-lg bg-[#2D6A4F] text-sm font-semibold flex items-center gap-1.5"
                >
                  <Check className="h-4 w-4" /> Confirmer
                </button>
                <button
                  data-testid={`credit-reject-${p.purchase_id}`}
                  onClick={() => act(p.purchase_id, "reject")}
                  className="h-9 px-3 rounded-lg bg-[#D00000]/70 text-sm font-semibold flex items-center gap-1.5"
                >
                  <X className="h-4 w-4" /> Rejeter
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
